import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { BRAND } from "../data/content";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: 2026",
    intro: `GK Brandify ("we", "us") respects your privacy. This policy explains what information we collect and how we use it when you visit our website or contact us.`,
    sections: [
      { h: "Information We Collect", p: "When you submit our contact form, we collect the details you provide — such as your name, phone number, email, business name, budget and project details — solely to respond to your enquiry." },
      { h: "How We Use Your Information", p: "We use your information only to reply to you, understand your project needs, and provide our services. We do not sell your personal data to third parties." },
      { h: "Cookies & Analytics", p: "We may use basic cookies or analytics to understand site usage and improve performance. You can disable cookies in your browser settings." },
      { h: "Data Security", p: "We take reasonable measures to protect your information. However, no method of transmission over the internet is 100% secure." },
      { h: "Your Rights", p: "You may request access to, correction of, or deletion of the personal information you have shared with us at any time." },
      { h: "Contact", p: `For any privacy questions, email us at ${BRAND.email} or call ${BRAND.phone}.` },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: 2026",
    intro: `By accessing this website and engaging GK Brandify's services, you agree to the following terms.`,
    sections: [
      { h: "Services", p: "GK Brandify provides branding, design, web development and digital marketing services. Specific deliverables, timelines and pricing are agreed in a separate project proposal." },
      { h: "Quotes & Payments", p: "Project quotes are tailored to scope. Payment terms and milestones are defined per project. Prices shown on the website are indicative starting points." },
      { h: "Intellectual Property", p: "Upon full payment, ownership of the final approved deliverables transfers to the client. GK Brandify may showcase the work in its portfolio unless otherwise agreed in writing." },
      { h: "Revisions & Scope", p: "Each package includes a defined number of revisions. Work beyond the agreed scope may be quoted separately." },
      { h: "Limitation of Liability", p: "GK Brandify is not liable for indirect or consequential losses. Our total liability is limited to the fees paid for the relevant project." },
      { h: "Contact", p: `For any questions about these terms, email us at ${BRAND.email} or call ${BRAND.phone}.` },
    ],
  },
};

export default function Legal({ type = "privacy" }) {
  const data = CONTENT[type];
  useEffect(() => { window.scrollTo(0, 0); }, [type]);

  return (
    <div className="min-h-screen bg-gk-black text-white grain">
      <div className="ambient-emerald" style={{ width: 500, height: 500, top: "-10%", right: "-10%", opacity: 0.25 }} />
      <div className="relative z-10 mx-auto max-w-3xl px-6 md:px-10 py-16">
        <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-2 font-inter text-sm text-white/60 hover:text-gk-champagne transition-colors mb-10">
          <ArrowLeft size={16} /> Back to Home
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <img src={BRAND.logo} alt="GK Brandify" className="w-9 h-9 rounded-lg object-cover ring-1 ring-gk-gold/30" />
          <span className="font-cinzel text-lg tracking-[0.22em] text-gold-metallic">GK BRANDIFY</span>
        </div>
        <h1 className="font-cinzel text-3xl sm:text-4xl font-bold text-white mt-6">{data.title}</h1>
        <p className="font-inter text-xs tracking-widest uppercase text-white/40 mt-2">{data.updated}</p>
        <p className="font-poppins text-white/65 leading-relaxed mt-6">{data.intro}</p>

        <div className="mt-10 space-y-8">
          {data.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-cinzel text-xl text-gk-champagne mb-2">{s.h}</h2>
              <p className="font-poppins text-sm md:text-base text-white/60 leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>

        <div className="hairline-gold w-full mt-14 mb-6 opacity-60" />
        <p className="font-inter text-xs text-white/40">© 2026 GK Brandify. Building Brands. Shaping the Future.</p>
      </div>
    </div>
  );
}
